import React, { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Autocomplete,
  DialogContent,
  Dialog,
  DialogActions,
  DialogTitle,
  Card,
  Box,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { format } from 'date-fns';
import { BiCloudUpload, BiCloudDownload } from 'react-icons/bi';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ExcelRenderer } from 'react-excel-renderer';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getBankDropdownData } from '../../store/Slice/Global/GlobalSlice';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import RevenueService from '../services/RevenueService';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';
import { PayoutApi } from '../../utils/Apis';
import { savefileApiData } from '../../store/Slice/Payout/PayoutSlice';

const { DownloadPayoutTemplate } = RevenueService;

function UploadBulkFilePayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploadfile, setUploadfile] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [fileDetails, setFileDetails] = useState({
    BankName: null
  });

  const { LoginData } = useLoginSelector();
  const { BankDrop } = useGlobalSelector();

  useEffect(() => {
    if (LoginData?.companyId && LoginData?.userId) {
      dispatch(getBankDropdownData({ companyId: LoginData.companyId, userId: LoginData.userId }));
    }
  }, [LoginData, dispatch]);

  const handleBankNameChange = (newValue) => {
    setFileDetails((prevData) => ({
      ...prevData,
      BankName: newValue
    }));
  };

  const handleRenderFile = (fileObj) => {
    ExcelRenderer(fileObj, (err) => {
      if (err) {
        console.error('Error reading excel file', err);
      }
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileName = selectedFile.name;
    const ext = fileName.split('.').pop().toLowerCase();

    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setUploadfile(null);
      setSelectedFileName('');
      dispatch(setApiError('Please upload only xlsx, xls or csv file'));
      return;
    }

    setUploadfile(selectedFile);
    setSelectedFileName(fileName);

    handleRenderFile(selectedFile);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await DownloadPayoutTemplate(LoginData?.jwt);
      const blob = await response.blob();
      const fileName = `Partner_Payout_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.xlsx`;
      const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(setApiSuccess('Template Successfully Downloaded'));
    } catch (error) {
      console.error('Export error:', error);
      dispatch(setApiError('Failed to download template'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // const bankId = fileDetails.BankName?.value;

    // if (!bankId) {
    //   dispatch(setApiError('Please select bank name'));
    //   return;
    // }

    if (!uploadfile) {
      dispatch(setApiError('Please upload MIS file'));
      return;
    }

    setIsUploading(true);
    try {
      const formUploadData = new FormData();
      formUploadData.append('file', uploadfile);
      // formUploadData.append('bankId', String(bankId));
      formUploadData.append('userId', String(LoginData?.userId));

      const result = await dispatch(savefileApiData(PayoutApi?.payoutUpload, formUploadData));

      if (!result) return;
      if (result?.data?.status === 'success') {
        setUploadSummary(result?.data);
        setOpenSummaryDialog(true);
      } else {
        dispatch(setApiError(result?.message || 'Upload failed'));
      }
    } catch (error) {
      console.error(error);
      dispatch(setApiError('Upload failed due to server error'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadInfo = () => {
    setOpenSummaryDialog(false);
    navigate('/payout');
  };

  return (
    <Page title="Partner Management System || Payout Upload">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
            sx={{ mb: 3 }}
          >
            <PageTitle info="Upload Payout Files" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Card
            sx={{
              p: 3.5,
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b', mb: 3 }}>
              File Details & Upload
            </Typography>

            <Grid container spacing={3}>
              {/* Bank Selection */}
              {/* <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#64748b',
                    mb: 0.8,
                    textTransform: 'uppercase'
                  }}
                >
                  Bank Name <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Autocomplete
                  disablePortal
                  id="combo-box-bank"
                  options={BankDrop || []}
                  size="small"
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Bank"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: '#f8fafc',
                          '& fieldset': { borderColor: '#e2e8f0' }
                        }
                      }}
                    />
                  )}
                  value={fileDetails.BankName || null}
                  onChange={(e, newValue) => handleBankNameChange(newValue)}
                />
              </Grid> */}

              {/* Download Template Box */}
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#64748b',
                    mb: 0.8,
                    textTransform: 'uppercase'
                  }}
                >
                  Template Reference
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleDownloadTemplate}
                  fullWidth
                  sx={{
                    height: 40,
                    borderRadius: '8px',
                    borderColor: '#13519d',
                    color: '#13519d',
                    bgcolor: 'rgba(19, 81, 157, 0.04)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    '&:hover': {
                      bgcolor: 'rgba(19, 81, 157, 0.1)',
                      borderColor: '#13519d'
                    }
                  }}
                >
                  <BiCloudDownload size={20} />
                  Download Payout Template
                </Button>
              </Grid>

              {/* Dropzone File Upload */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#64748b',
                    mb: 0.8,
                    textTransform: 'uppercase'
                  }}
                >
                  Upload File <span style={{ color: '#ef4444' }}>*</span>
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    py: 5,
                    border: selectedFileName ? '2px dashed #13519d' : '2px dashed #cbd5e1',
                    bgcolor: selectedFileName ? 'rgba(19, 81, 157, 0.02)' : '#f8fafc',
                    color: '#64748b',
                    borderRadius: '12px',
                    textTransform: 'none',
                    flexDirection: 'column',
                    gap: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      borderColor: '#13519d',
                      color: '#13519d'
                    }
                  }}
                >
                  {selectedFileName ? (
                    <InsertDriveFileIcon sx={{ fontSize: 44, color: '#13519d' }} />
                  ) : (
                    <BiCloudUpload size={46} style={{ color: '#13519d' }} />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: selectedFileName ? '#13519d' : '#1e293b'
                    }}
                  >
                    {selectedFileName
                      ? selectedFileName
                      : 'Click to select or drag & drop payout file'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Supported file formats: .xlsx, .xls, .csv
                  </Typography>
                  <input accept=".xlsx,.xls,.csv" hidden type="file" onChange={handleFileChange} />
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3.5, borderColor: '#e2e8f0' }} />

            <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
              <Button
                variant="outlined"
                onClick={() => navigate('/payout')}
                sx={{
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  '&:hover': { bgcolor: '#f1f5f9' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={isUploading}
                onClick={handleSubmit}
                sx={{
                  borderRadius: '8px',
                  px: 4,
                  py: 1,
                  bgcolor: '#13519d',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(19, 81, 157, 0.25)',
                  '&:hover': { bgcolor: '#0d3d75' }
                }}
              >
                {isUploading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} color="inherit" />
                    <span>Uploading...</span>
                  </Stack>
                ) : (
                  'Submit'
                )}
              </Button>
            </Stack>
          </Card>
        </Box>

        {/* Upload Summary Dialog */}
        <Dialog
          open={openSummaryDialog}
          onClose={() => setOpenSummaryDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 2,
              px: 3,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              Upload Summary
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenSummaryDialog(false)}
              sx={{ color: '#64748b' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', my: 1 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 52, color: '#16a34a', mb: 1 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1e293b', mb: 2 }}>
                {uploadSummary?.message || 'File uploaded and processed successfully'}
              </Typography>
            </Box>

            <Stack
              spacing={1.5}
              sx={{ bgcolor: '#fafbfc', p: 2, borderRadius: '12px', border: '1px solid #e2e8f0' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Total Records:
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                  {uploadSummary?.summary?.processedCount}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
                  Successfully Uploaded:
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>
                  {uploadSummary?.summary?.successfulCount}
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: '#e2e8f0' }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
                  Failed Records:
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                  {uploadSummary?.summary?.failedCount}
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Button
              onClick={handleUploadInfo}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: '8px',
                bgcolor: '#13519d',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: '#0d3d75' }
              }}
            >
              Continue to Payout List
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}

export default UploadBulkFilePayout;
